import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Badge, Button, Checkbox } from '../atoms';
import { useToast } from './Toast';
import { sanitizeHtml } from '../lib/sanitizeHtml';
import { mailErrorMessage, type MailConnection } from '../lib/mail';

/**
 * OrgEmailSettings — Org Settings → Email (spec 0014, AC-6 / AC-7 / AC-13).
 *
 *   • Compliance note: appended after the signature on every email a user pushes to Outlook
 *     (never on system mail), shown read-only in the composer. Super-admin write.
 *   • Signature template: the starting point of each user's signature, with {{name}} {{title}}
 *     {{email}} {{phone}} {{firm}}.
 *   • Connections: every staff user with their Outlook status, and Unlink for someone who left.
 *   • The Microsoft 365 tenant consent link, for the customer's tenant admin to follow once.
 */
interface ConnectionsTable {
  configured: boolean;
  admin_consent_url: string | null;
  users: Array<{ user_id: string; name: string; email: string; role: string; connection: MailConnection | null }>;
}

export function OrgEmailSettings({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const note = useQuery<{ email_compliance_note: { enabled: boolean; html: string } }>({
    queryKey: ['org-settings', 'email-compliance-note'],
    queryFn: ({ signal }) => api.get('/api/org-settings/email-compliance-note', signal), retry: false,
  });
  const template = useQuery<{ signature_template: { html: string } }>({
    queryKey: ['org-settings', 'signature-template'],
    queryFn: ({ signal }) => api.get('/api/org-settings/signature-template', signal), retry: false,
  });
  const table = useQuery<ConnectionsTable>({
    queryKey: ['mail-connections', 'org'],
    queryFn: ({ signal }) => api.get('/api/mail-connections', signal), retry: false,
  });

  const [noteOn, setNoteOn] = useState(false);
  const [noteHtml, setNoteHtml] = useState('');
  const [templateHtml, setTemplateHtml] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const noteValue = note.data?.email_compliance_note;
  const templateValue = template.data?.signature_template;
  const users = Array.isArray(table.data?.users) ? table.data!.users : [];
  useEffect(() => { if (noteValue) { setNoteOn(noteValue.enabled === true); setNoteHtml(noteValue.html ?? ''); } }, [noteValue]);
  useEffect(() => { if (templateValue) setTemplateHtml(templateValue.html ?? ''); }, [templateValue]);

  async function put(slug: string, body: Record<string, unknown>, done: string) {
    setSaving(slug);
    try {
      await api.put(`/api/org-settings/${slug}`, body);
      await queryClient.invalidateQueries({ queryKey: ['org-settings', slug] });
      toast({ variant: 'success', message: done });
    } catch (err) {
      toast({ variant: 'error', message: mailErrorMessage(err) });
    } finally { setSaving(null); }
  }
  async function unlink(userId: string, name: string) {
    setSaving(`unlink-${userId}`);
    try {
      await api.delete(`/api/mail-connections/users/${userId}`);
      await queryClient.invalidateQueries({ queryKey: ['mail-connections', 'org'] });
      toast({ variant: 'info', message: `${name}'s Outlook is unlinked.` });
    } catch (err) {
      toast({ variant: 'error', message: mailErrorMessage(err) });
    } finally { setSaving(null); }
  }

  const card = { background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', marginTop: 'var(--sp-4)' } as const;
  const h3 = { margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' } as const;
  const hint = { fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', margin: 0 } as const;
  const area = { width: '100%', fontFamily: 'var(--ff-mono, monospace)', fontSize: 'var(--fs-xs)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-2)' } as const;

  return (
    <div data-testid="org-email-settings">
      <div style={card}>
        <h3 style={h3}>Compliance note</h3>
        <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
          <Checkbox label="Add the compliance note to every email users push to Outlook" checked={noteOn} disabled={!canEdit || !noteValue} onChange={(e) => setNoteOn(e.target.checked)}
            description="It goes after the user's signature, below a divider, and users see it read-only while they write. System mail such as invitations and password resets does not carry it." />
          <textarea aria-label="Compliance note HTML" rows={4} value={noteHtml} disabled={!canEdit || !noteValue} onChange={(e) => setNoteHtml(e.target.value)} style={area} />
          {noteHtml && <div data-testid="compliance-note-preview" style={{ border: '1px dashed var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-2)', fontSize: 'var(--fs-sm)' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(noteHtml) }} />}
          <div><Button variant="primary" size="sm" disabled={!canEdit || !noteValue} loading={saving === 'email-compliance-note'}
            onClick={() => put('email-compliance-note', { email_compliance_note: { enabled: noteOn, html: noteHtml } }, noteOn ? 'Compliance note saved and on.' : 'Compliance note saved (off).')}>Save note</Button></div>
        </div>
      </div>

      <div style={card}>
        <h3 style={h3}>Signature template</h3>
        <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
          <p style={hint}>Each user starts their signature from this. {'{{name}}'}, {'{{title}}'}, {'{{email}}'}, {'{{phone}}'} and {'{{firm}}'} fill from the user; a field they have not filled in is left out.</p>
          <textarea aria-label="Signature template HTML" rows={4} value={templateHtml} disabled={!canEdit || !templateValue} onChange={(e) => setTemplateHtml(e.target.value)} style={area} />
          <div><Button variant="primary" size="sm" disabled={!canEdit || !templateValue} loading={saving === 'signature-template'}
            onClick={() => put('signature-template', { signature_template: { html: templateHtml } }, 'Signature template saved.')}>Save template</Button></div>
        </div>
      </div>

      <div style={card}>
        <h3 style={h3}>Outlook connections</h3>
        {table.data && table.data.configured === false && <p style={hint}>The Outlook connector is not configured for this deployment, so nobody can connect yet.</p>}
        {table.data?.admin_consent_url && (
          <p style={{ ...hint, marginBottom: 'var(--sp-3)' }}>
            Your Microsoft 365 administrator approves appealstack once for the whole organisation:{' '}
            <a href={table.data.admin_consent_url} target="_blank" rel="noreferrer">Microsoft 365 tenant consent</a>.
            appealstack asks to read the user&apos;s profile and to write drafts in their mailbox (User.Read, Mail.ReadWrite, offline access). It never sends mail.
          </p>
        )}
        {users.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-ink-3)' }}><th style={{ padding: 'var(--sp-1)' }}>User</th><th>Outlook</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: 'var(--sp-2) var(--sp-1)' }}>{u.name}<div style={hint}>{u.email}</div></td>
                  <td>{u.connection?.account_email ?? '—'}</td>
                  <td>
                    {!u.connection && <Badge variant="neutral">Not connected</Badge>}
                    {u.connection?.status === 'connected' && <Badge variant="success">Connected</Badge>}
                    {u.connection?.status === 'error' && <Badge variant="danger">Needs reconnecting</Badge>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {u.connection && <Button variant="ghost" size="sm" aria-label={`Unlink ${u.name}`} loading={saving === `unlink-${u.user_id}`} onClick={() => unlink(u.user_id, u.name)}>Unlink</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
